import { useCallback, useEffect, useMemo, useState } from 'react';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { createQueryClient } from '@/api/queryClient';
import { VisitTracker } from '@/analytics/VisitTracker';
import type { PublicConfig } from '@/api/types';
import { AuthProvider, type AuthAdapter } from '@/auth';
import { NotificationsProvider } from '@/components/feedback/NotificationsProvider';
import { buildLandingRoutes } from '@/landing/routes';
import { buildMarketplaceRoutes } from '@/marketplace/routes';
import { buildRoutes } from '@/router';
import { applyDocumentBranding, themeOptionsFor } from '@/tenant/applyBranding';
import { loadTenantConfig } from '@/tenant/loadTenantConfig';
import { tenantContextValue } from '@/tenant/context';
import { TenantProvider } from '@/tenant/TenantContext';
import { createAppTheme } from '@/theme';
import { loadFont } from '@/theme/fonts';

interface AppProps {
  initialConfig: PublicConfig;
  adapter: AuthAdapter;
}

export function App({ initialConfig, adapter }: AppProps) {
  const [config, setConfig] = useState(initialConfig);
  const [queryClient] = useState(createQueryClient);
  const theme = useMemo(() => createAppTheme(themeOptionsFor(config)), [config]);

  useEffect(() => {
    const primary = theme.colorSchemes.light?.palette.primary.main ?? '#5B2DB0';
    applyDocumentBranding(config, primary);
  }, [config, theme]);

  const refresh = useCallback(async () => {
    const result = await loadTenantConfig();
    if (result.kind === 'ready') {
      await loadFont(themeOptionsFor(result.config).font);
      setConfig(result.config);
    }
  }, []);
  const tenant = useMemo(() => tenantContextValue(config, refresh), [config, refresh]);
  const router = useMemo(
    () =>
      createBrowserRouter(
        tenant.isLanding
          ? buildLandingRoutes()
          : tenant.isMarketplace
            ? buildMarketplaceRoutes()
            : buildRoutes(tenant.isPlatform),
      ),
    [tenant.isPlatform, tenant.isMarketplace, tenant.isLanding],
  );
  const clearServerState = useCallback(() => queryClient.clear(), [queryClient]);

  return (
    <ThemeProvider theme={theme} defaultMode="system">
      <CssBaseline enableColorScheme />
      <QueryClientProvider client={queryClient}>
        <TenantProvider value={tenant}>
          <AuthProvider adapter={adapter} onSignedOut={clearServerState}>
            <NotificationsProvider>
              <RouterProvider router={router} />
              <VisitTracker router={router} enabled={!tenant.isPlatform} />
            </NotificationsProvider>
          </AuthProvider>
        </TenantProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
